import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { PageLayout } from "../components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Upload,
  FileText,
  Trash2,
  Download,
  File,
  Image,
  Video,
  FileSpreadsheet,
  Plus,
  Youtube,
  Link,
  ExternalLink,
} from "lucide-react";

const API = `${window.location.origin}/api`;

const getFileIcon = (fileType) => {
  if (fileType?.includes("youtube") || fileType === "youtube") return <Youtube className="w-5 h-5 text-red-600" />;
  if (fileType?.includes("pdf")) return <FileText className="w-5 h-5 text-red-500" />;
  if (fileType?.includes("image")) return <Image className="w-5 h-5 text-blue-500" />;
  if (fileType?.includes("video")) return <Video className="w-5 h-5 text-purple-500" />;
  if (fileType?.includes("spreadsheet") || fileType?.includes("excel")) return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
  return <File className="w-5 h-5 text-gray-500" />;
};

const AdminMaterialsPage = () => {
  const [sessions, setSessions] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState("all");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    session_id: "",
    name: "",
    file: null,
    uploadType: "file", // "file" o "youtube"
    youtubeUrl: "",
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sessionsRes, materialsRes] = await Promise.all([
        axios.get(`${API}/sessions`),
        axios.get(`${API}/admin/materials`),
      ]);
      setSessions(sessionsRes.data);
      setMaterials(materialsRes.data || []);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm({
        ...uploadForm,
        file,
        name: uploadForm.name || file.name.replace(/\.[^/.]+$/, ""),
      });
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.session_id || !uploadForm.name) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    // Validazione in base al tipo
    if (uploadForm.uploadType === "file" && !uploadForm.file) {
      toast.error("Seleziona un file da caricare");
      return;
    }
    
    if (uploadForm.uploadType === "youtube" && !uploadForm.youtubeUrl) {
      toast.error("Inserisci il link YouTube");
      return;
    }

    // Validazione URL YouTube
    if (uploadForm.uploadType === "youtube") {
      const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/)|youtu\.be\/)[a-zA-Z0-9_-]+/;
      if (!youtubeRegex.test(uploadForm.youtubeUrl)) {
        toast.error("Inserisci un link YouTube valido");
        return;
      }
    }

    setUploading(true);
    try {
      let fileUrl, fileType;

      if (uploadForm.uploadType === "youtube") {
        // Per YouTube, salva direttamente il link
        fileUrl = uploadForm.youtubeUrl;
        fileType = "youtube";
      } else {
        // Upload file normale
        const formData = new FormData();
        formData.append("file", uploadForm.file);
        
        const uploadRes = await axios.post(`${API}/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        fileUrl = `${window.location.origin}${uploadRes.data.url}`;
        fileType = uploadForm.file.type;
      }

      // Crea il record del materiale
      await axios.post(`${API}/admin/sessions/${uploadForm.session_id}/materials`, {
        name: uploadForm.name,
        file_type: fileType,
        file_url: fileUrl,
      });

      toast.success("Materiale caricato con successo!");
      setUploadDialogOpen(false);
      setUploadForm({ session_id: "", name: "", file: null, uploadType: "file", youtubeUrl: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il caricamento");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!materialToDelete) return;

    try {
      await axios.delete(`${API}/admin/materials/${materialToDelete.material_id}`);
      toast.success("Materiale eliminato");
      fetchData();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    } finally {
      setDeleteDialogOpen(false);
      setMaterialToDelete(null);
    }
  };

  const filteredMaterials = selectedSession === "all"
    ? materials
    : materials.filter((m) => m.session_id === selectedSession || m.course_id === selectedSession);

  const getSessionTitle = (sessionId) => {
    const session = sessions.find((s) => s.session_id === sessionId);
    return session ? session.title : "Sessione non trovata";
  };

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-2">
              Materiali Didattici
            </h1>
            <p className="text-muted-foreground">
              Carica dispense, PDF, video e altri materiali per i corsi
            </p>
          </div>
          <Button
            onClick={() => setUploadDialogOpen(true)}
            className="rounded-full mt-4 md:mt-0"
            data-testid="upload-material-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Carica Materiale
          </Button>
        </div>

        {/* Filter by session */}
        <Card className="rounded-2xl border-border/50 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Label className="whitespace-nowrap">Filtra per sessione:</Label>
              <Select value={selectedSession} onValueChange={setSelectedSession}>
                <SelectTrigger className="rounded-xl max-w-md">
                  <SelectValue placeholder="Tutte le sessioni" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le sessioni</SelectItem>
                  {sessions.map((session) => (
                    <SelectItem key={session.session_id} value={session.session_id}>
                      {session.title} ({session.date})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Materials list */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-secondary rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredMaterials.length > 0 ? (
          <div className="space-y-3">
            {filteredMaterials.map((material) => (
              <Card key={material.material_id} className="rounded-xl border-border/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                      {getFileIcon(material.file_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium truncate">{material.name}</h4>
                      <p className="text-sm text-muted-foreground truncate">
                        {getSessionTitle(material.session_id || material.course_id)}
                      </p>
                    </div>
                    <Badge variant="secondary" className="rounded-full hidden sm:flex">
                      {material.file_type === "youtube" ? "YOUTUBE" : material.file_type?.split("/")[1]?.toUpperCase() || "FILE"}
                    </Badge>
                    <div className="flex gap-2">
                      <a href={material.file_url} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" className="rounded-full">
                          {material.file_type === "youtube" ? (
                            <ExternalLink className="w-4 h-4" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </Button>
                      </a>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-full text-destructive hover:text-destructive"
                        onClick={() => {
                          setMaterialToDelete(material);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="rounded-2xl border-border/50">
            <CardContent className="py-16 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground mb-4">Nessun materiale caricato</p>
              <Button onClick={() => setUploadDialogOpen(true)} className="rounded-full">
                Carica il primo materiale
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Info card */}
        <Card className="rounded-2xl border-border/50 mt-6 bg-blue-50/50">
          <CardContent className="p-6">
            <h3 className="font-medium mb-2 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Formati Supportati
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-muted-foreground">
              <div>📄 PDF</div>
              <div>📝 Word (DOCX)</div>
              <div>📊 Excel (XLSX)</div>
              <div>🖼️ Immagini (JPG, PNG)</div>
              <div>🎥 Video (MP4, MOV)</div>
              <div>📑 PowerPoint (PPTX)</div>
              <div>📁 ZIP</div>
              <div>📋 Testo (TXT)</div>
              <div className="text-red-600 font-medium">▶️ YouTube (Link)</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Carica Materiale</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Sessione *</Label>
              <Select
                value={uploadForm.session_id}
                onValueChange={(v) => setUploadForm({ ...uploadForm, session_id: v })}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Seleziona sessione" />
                </SelectTrigger>
                <SelectContent>
                  {sessions.map((session) => (
                    <SelectItem key={session.session_id} value={session.session_id}>
                      {session.title} ({session.date})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Nome Materiale *</Label>
              <Input
                value={uploadForm.name}
                onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                placeholder="es. Dispensa Modulo 1"
                className="rounded-xl"
              />
            </div>

            {/* Tipo di caricamento */}
            <div className="space-y-2">
              <Label>Tipo di Materiale *</Label>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant={uploadForm.uploadType === "file" ? "default" : "outline"}
                  className="rounded-xl h-auto py-3 flex flex-col items-center gap-1"
                  onClick={() => setUploadForm({ ...uploadForm, uploadType: "file", youtubeUrl: "" })}
                >
                  <Upload className="w-5 h-5" />
                  <span className="text-sm">Carica File</span>
                </Button>
                <Button
                  type="button"
                  variant={uploadForm.uploadType === "youtube" ? "default" : "outline"}
                  className="rounded-xl h-auto py-3 flex flex-col items-center gap-1"
                  onClick={() => setUploadForm({ ...uploadForm, uploadType: "youtube", file: null })}
                >
                  <Youtube className="w-5 h-5" />
                  <span className="text-sm">Link YouTube</span>
                </Button>
              </div>
            </div>

            {/* Input per File */}
            {uploadForm.uploadType === "file" && (
              <div className="space-y-2">
                <Label>File *</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.mp4,.mov,.zip,.txt"
                />
                {uploadForm.file ? (
                  <div className="flex items-center gap-3 p-3 bg-secondary rounded-xl">
                    {getFileIcon(uploadForm.file.type)}
                    <span className="flex-1 truncate">{uploadForm.file.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setUploadForm({ ...uploadForm, file: null })}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full rounded-xl h-24 border-dashed"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="flex flex-col items-center">
                      <Upload className="w-8 h-8 mb-2 text-muted-foreground" />
                      <span>Clicca per selezionare un file</span>
                      <span className="text-xs text-muted-foreground mt-1">
                        PDF, Word, Excel, Immagini, Video
                      </span>
                    </div>
                  </Button>
                )}
              </div>
            )}

            {/* Input per YouTube */}
            {uploadForm.uploadType === "youtube" && (
              <div className="space-y-2">
                <Label>Link Video YouTube *</Label>
                <div className="relative">
                  <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-600" />
                  <Input
                    value={uploadForm.youtubeUrl}
                    onChange={(e) => setUploadForm({ ...uploadForm, youtubeUrl: e.target.value })}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="rounded-xl pl-10"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Incolla il link del video YouTube (es. https://www.youtube.com/watch?v=xxxxx o https://youtu.be/xxxxx)
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => setUploadDialogOpen(false)}
            >
              Annulla
            </Button>
            <Button
              className="rounded-full"
              onClick={handleUpload}
              disabled={uploading || !uploadForm.session_id || !uploadForm.name || 
                (uploadForm.uploadType === "file" && !uploadForm.file) ||
                (uploadForm.uploadType === "youtube" && !uploadForm.youtubeUrl)}
            >
              {uploading ? "Caricamento..." : "Carica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina Materiale</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare "{materialToDelete?.name}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
};

export default AdminMaterialsPage;
